import { Injectable } from '@angular/core';
import { DATA_TYPE } from '@constants/data-type.constant';
import { UserDefinedWidgetType } from '@constants/pages/user-defined-page/user-defined-widget-type';
import { Signal } from '@models/dashboard/signal';
import { ChartAxisSettings } from '@models/pages/user-defined-page/chart-axis-settings';
import { ChartSignalEntry } from '@models/pages/user-defined-page/chart-signal-entry';
import { UserDefinedWidget } from '@models/pages/user-defined-page/user-defined-widget';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class ChartsConfigurationHelperService {

  hasDeletedSignals$: BehaviorSubject<Map<string, boolean>> = new BehaviorSubject(new Map<string, boolean>());

  private NON_STRING_TYPES: DATA_TYPE[] = [DATA_TYPE.FLOAT, DATA_TYPE.INTEGER, DATA_TYPE.BOOLEAN, DATA_TYPE.BOOL, DATA_TYPE.FLOAT64, DATA_TYPE.INT64];
  private ALL_TYPES: DATA_TYPE[] = [DATA_TYPE.FLOAT, DATA_TYPE.INTEGER, DATA_TYPE.BOOLEAN, DATA_TYPE.BOOL, DATA_TYPE.FLOAT64, DATA_TYPE.INT64, DATA_TYPE.BYTES, DATA_TYPE.STRING, DATA_TYPE.TIMESTAMP];

  constructor(private translation: TranslateService) {
  }

  public determineTitle(widgetType: string) {
    let title = '';
    switch (UserDefinedWidgetType[widgetType]) {
      case UserDefinedWidgetType.ANALYSIS_LINE: {
        title = this.translation.instant('Interactive Data Analysis widget');
        break;
      }

      case UserDefinedWidgetType.BAR: {
        title = this.translation.instant('Bar widget');
        break;
      }

      case UserDefinedWidgetType.GAUGE: {
        title = this.translation.instant('Gauge widget');
        break;
      }

      case UserDefinedWidgetType.IMAGE: {
        title = this.translation.instant('Image widget');
        break;
      }

      case UserDefinedWidgetType.LINE_WITH_SIGNALS_PER_AXIS: {
        title = this.translation.instant('Line widget');
        break;
      }

      case UserDefinedWidgetType.MAP: {
        title = this.translation.instant('Map widget');
        break;
      }

      case UserDefinedWidgetType.PIE: {
        title = this.translation.instant('Pie widget');
        break;
      }
      case UserDefinedWidgetType.STATUS: {
        title = this.translation.instant('Status widget');
        break;
      }
      case UserDefinedWidgetType.TEXT: {
        title = this.translation.instant('Text widget');
        break;
      }
      case UserDefinedWidgetType.COMMAND: {
        title = this.translation.instant('Command widget');
        break;
      }
    }
    return title;
  }

  public determineWidgetTypeIconCssClass(widgetType: string) {
    let cssClass = '';
    switch (UserDefinedWidgetType[widgetType]) {
      case UserDefinedWidgetType.ANALYSIS_LINE: {
        cssClass = 'fa-solid fa-chart-area';
        break;
      }

      case UserDefinedWidgetType.BAR: {
        cssClass = 'fa-solid fa-chart-column';
        break;
      }

      case UserDefinedWidgetType.GAUGE: {
        cssClass = 'fa-solid fa-gauge';
        break;
      }

      case UserDefinedWidgetType.IMAGE: {
        cssClass = 'fa-solid fa-image';
        break;
      }

      case UserDefinedWidgetType.LINE_WITH_SIGNALS_PER_AXIS: {
        cssClass = 'fa-solid fa-chart-line';
        break;
      }

      case UserDefinedWidgetType.MAP: {
        cssClass = 'fa-solid fa-map-location-dot';
        break;
      }

      case UserDefinedWidgetType.PIE: {
        cssClass = 'fa-solid fa-chart-pie';
        break;
      }
      case UserDefinedWidgetType.STATUS: {
        cssClass = 'fa-solid fa-circle-exclamation';
        break;
      }
      case UserDefinedWidgetType.TEXT: {
        cssClass = 'fa-solid fa-list';
        break;
      }
      case UserDefinedWidgetType.COMMAND: {
        cssClass = 'fa-solid fa-server';
        break;
      }


    }
    return cssClass;
  }

  public filterMisconfiguredSignals(signals: Signal[]): Signal[] {
    const filteredSignals: Signal[] = [];
    if (!signals || signals.length === 0) {
      return filteredSignals;
    }

    for (const signal of signals) {
      if (this.signalIsWellDefined(signal, this.ALL_TYPES) && !filteredSignals.includes(signal)) {
        filteredSignals.push(signal);
      }
    }

    return filteredSignals;
  }

  public filterMisconfiguredOrUnusableSignals(signals: Signal[]): Signal[] {
    const filteredSignals: Signal[] = [];
    if (!signals || signals.length === 0) {
      return filteredSignals;
    }

    for (const signal of signals) {
      if (this.signalIsWellDefined(signal, this.NON_STRING_TYPES) && !filteredSignals.includes(signal)) {
        filteredSignals.push(signal);
      }
    }

    return filteredSignals;
  }

  public validateLineWidgetConfiguration(widget: UserDefinedWidget): boolean {
    if (!widget || !widget.widgetDetails) {
      return false;
    }
    const widgetDetails = widget.widgetDetails;
    if (!widgetDetails.widgetType || !widgetDetails.widgetName || widgetDetails.widgetName.isNullOrWhitespace()) {
      return false;
    }

    const chartDetails = widget.userDefinedChartDetails;
    if (!chartDetails || !chartDetails.rangeValue || chartDetails.rangeValue < 1) {
      return false;
    }

    const axisArray = chartDetails.axis;
    if (!axisArray || axisArray.length === 0) {
      return false;
    }

    for (const axis of axisArray) {
      if (!this.verifyLineWidgetAxisSettings(axis)) {
        return false;
      }
    }


    return true;
  }

  public convertNumberToHexColor(color: number): string {
    let hex = color.toString(16).toUpperCase();
    while (hex.length < 6) {
      hex = '0' + hex;
    }
    return '#' + hex;
  }

  public convertHexColorToNumber(color: string): number {
    const normalizedColor = this.normalizeColorToHex(color);
    return parseInt(normalizedColor.replace(/^#/, ''), 16);
  }

  public normalizeColorToHex(color: string): string {
    const value = color?.trim();
    if (!value) {
      return '';
    }
    if (/^#[0-9a-f]{3}$/i.test(value)) {
      return '#' + value.slice(1).split('').map(part => part + part).join('').toUpperCase();
    }
    if (/^#[0-9a-f]{6}$/i.test(value)) {
      return value.toUpperCase();
    }
    const namedColors: Record<string, string> = {
      black: '#000000',
      blue: '#0000FF',
      gray: '#808080',
      grey: '#808080',
      green: '#008000',
      red: '#FF0000',
      white: '#FFFFFF',
      yellow: '#FFFF00',
    };
    const namedColor = namedColors[value.toLowerCase()];
    if (namedColor) {
      return namedColor;
    }
    if (typeof document !== 'undefined') {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (context) {
        context.fillStyle = value;
        const normalized = context.fillStyle;
        if (/^#[0-9a-f]{6}$/i.test(normalized)) {
          return normalized.toUpperCase();
        }
      }
    }
    return value.toUpperCase();
  }

  public getUsedSignalsIdsOnAllAxis(axisArray: ChartAxisSettings[]): number[] {
    const signalIds: number[] = [];
    if (!axisArray || axisArray.length === 0) {
      return signalIds;
    }

    for (const axis of axisArray) {
      if (!axis || !axis.signals || axis.signals.length === 0) {
        continue;
      }
      const axisSignals = axis.signals;
      for (const signal of axisSignals) {
        if (signal && signal.signalId && !signalIds.includes(signal.signalId)) {
          signalIds.push(signal.signalId);
        }
      }
    }

    return signalIds;
  }

  public getUsedSignalsNameOnAllAxis(axisArray: ChartAxisSettings[]): string[] {
    const signalNames: string[] = [];
    if (!axisArray || axisArray.length === 0) {
      return signalNames;
    }

    for (const axis of axisArray) {
      if (!axis || !axis.signals || axis.signals.length === 0) {
        continue;
      }
      const axisSignals = axis.signals;
      for (const signal of axisSignals) {
        if (signal && signal.signalName && !signalNames.includes(signal.signalName)) {
          signalNames.push(signal.signalName);
        }
      }
    }

    return signalNames;
  }

  public getSelectedSignalIds(signals: { signalName: string }[], allUsableSignals: Signal[]): number[] {
    return signals.reduce((acc: number[], next) => {
      const selectSignal: Signal | undefined = allUsableSignals.find(s => s.name.toLowerCase() === next.signalName.toLowerCase());
      if (selectSignal && selectSignal.id) {
        acc.push(selectSignal.id);
      }
      return acc;
    }, []);
  }

  public getSelectedSignalIdsById(signals: ChartSignalEntry[], allUsableSignals: Signal[]): number[] {
    return signals.reduce((acc: number[], next) => {
      const selectSignal: Signal | undefined = allUsableSignals.find(s => s.id === next.signalId);
      if (selectSignal && selectSignal.id) {
        acc.push(selectSignal.id);
      }
      return acc;
    }, []);
  }

  private verifyLineWidgetAxisSettings(axis: ChartAxisSettings) {
    if (!axis || !axis.axisYLabel || axis.axisYLabel.isNullOrWhitespace()) {
      return false;
    }

    if (!axis.signals || axis.signals.length === 0) {
      return false;
    }
    if (axis.minY === 0 && axis.maxY === 0) {
      return true;
    }

    return axis.minY < axis.maxY;

  }

  private signalIsWellDefined(signal: Signal, dataTypes: DATA_TYPE[]): boolean {
    if (!signal) {
      return false;
    }

    if (!signal.signal) {
      return false;
    }

    if (!signal.table) {
      return false;
    }

    if (!signal.id) {
      return false;
    }

    for (const dataType of dataTypes) {
      if (dataType === signal.type) {
        return true;
      }
    }

    return false;
  }

}
